import { useSyncExternalStore } from 'react';

/**
 * The slot a customer picked, held from the product page to checkout (§4).
 *
 * Separate from the cart on purpose. A booking is not a line item: there is
 * exactly one per order, it has no quantity, and it must not survive a change
 * of shop or a completed checkout. Folding it into `CartItem` would give every
 * retail line three fields it can never use.
 *
 * Kept in `sessionStorage` rather than `localStorage` — a slot chosen last
 * Tuesday is worse than no slot at all, and unlike a cart there is nothing to
 * recover from a dropped connection: the times are re-fetched on every visit.
 */

export interface BookingSelection {
  productId: string;
  /** ISO instant. §6.5: one timezone, so no offset travels with it. */
  startsAt: string;
  resourceId: string;
}

const STORAGE_KEY = 'telestore.booking.v1';

function load(): BookingSelection | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<BookingSelection>;
    if (!parsed.productId || !parsed.startsAt || !parsed.resourceId) return null;
    return parsed as BookingSelection;
  } catch {
    return null;
  }
}

let state: BookingSelection | null = load();
const listeners = new Set<() => void>();

function emit() {
  try {
    if (state) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private browsing — the selection still holds for this page.
  }
  for (const listener of listeners) listener();
}

export const booking = {
  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  snapshot(): BookingSelection | null {
    return state;
  },

  set(selection: BookingSelection) {
    state = selection;
    emit();
  },

  clear() {
    if (state === null) return;
    state = null;
    emit();
  },
};

export function useBooking(): BookingSelection | null {
  return useSyncExternalStore(booking.subscribe, booking.snapshot, booking.snapshot);
}
